import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaInfoCircle } from 'react-icons/fa';

/**
 * DisclosureTopicTooltip Component
 * Shows a tooltip with IFRS disclosure topic definition on hover
 * Uses React Portal to render outside modal boundaries
 * 
 * @param {string} disclosureTopic - The disclosure topic name to display
 * @param {string} disclosureDescription - The IFRS definition/description
 * @param {string} className - Additional CSS classes
 * @param {boolean} showIcon - Whether to show info icon (default: true)
 */
const DisclosureTopicTooltip = ({ 
  disclosureTopic, 
  disclosureDescription, 
  className = '', 
  showIcon = true,
  children 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  // Calculate tooltip position - hooks must be called unconditionally
  useEffect(() => {
    if (isHovered && triggerRef.current && disclosureDescription) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 384; // w-96 = 384px
      const tooltipHeight = 200; // Approximate height
      
      // Position above the trigger element, centered
      const left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      const top = rect.top - tooltipHeight - 8; // 8px margin
      
      // Adjust if tooltip would go off screen
      const adjustedLeft = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
      const adjustedTop = top < 8 ? rect.bottom + 8 : top;
      
      setTooltipPosition({ top: adjustedTop, left: adjustedLeft });
    }
  }, [isHovered, disclosureDescription]);

  // Don't render tooltip if no description
  if (!disclosureDescription) {
    return children || <span className={className}>{disclosureTopic}</span>;
  }

  return (
    <>
      <span 
        ref={triggerRef}
        className={`${className} ${showIcon ? 'cursor-help' : ''} inline-flex items-center gap-1 relative`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children || disclosureTopic}
        {showIcon && (
          <FaInfoCircle className="w-3 h-3 text-blue-500 opacity-70 hover:opacity-100 transition-opacity" />
        )}
      </span>
      
      {isHovered && typeof document !== 'undefined' && createPortal(
        <div 
          ref={tooltipRef}
          className="fixed z-[99999] w-80 sm:w-96 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl border border-gray-700 pointer-events-none"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
        >
          <div className="font-semibold text-sm mb-2 text-blue-300 border-b border-gray-700 pb-1">
            {disclosureTopic}
          </div>
          <div className="text-gray-100 leading-relaxed">
            {disclosureDescription}
          </div>
          {/* Arrow pointing down */}
          <div 
            className="absolute top-full left-1/2 transform -translate-x-1/2"
            style={{
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid #1f2937'
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default DisclosureTopicTooltip;

